import React, { useState, useCallback, useEffect } from 'react';
import { Shuffle, RefreshCw } from 'lucide-react';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
};

// ══════════════════════════════════════════════════════════════════
//  KOLASE FOTO — Cara menambahkan foto:
//
//  1. Simpan semua foto di folder: /public/images/collage/
//  2. Ganti setiap "src: null" dengan path foto, contoh:
//     src: "/images/collage/foto1.jpg"
//  3. Ubah "caption" sesuai keterangan foto kamu
//
//  Bisa tambah lebih banyak foto dengan copy-paste objek baru.
//  Fitur shuffle akan mengacak urutan otomatis setiap 6 detik.
// ══════════════════════════════════════════════════════════════════
const ALL_PHOTOS = [
  { id: 1, src: '/images/collage/foto (1).JPEG', caption: 'Foto 1', label: 'foto (1).JPEG', accent: '#00f5ff' },
  { id: 2, src: '/images/collage/foto (1).JPG',  caption: 'Foto 2', label: 'foto (1).JPG',  accent: '#7b2fff' },
  { id: 3, src: '/images/collage/foto (2).JPEG', caption: 'Foto 3', label: 'foto (2).JPEG', accent: '#00ff88' },
  { id: 4, src: '/images/collage/foto (2).jpg',  caption: 'Foto 4', label: 'foto (2).jpg',  accent: '#00f5ff' },
  { id: 5, src: '/images/collage/foto (3).JPEG', caption: 'Foto 5', label: 'foto (3).JPEG', accent: '#7b2fff' },
  { id: 6, src: '/images/collage/foto (3).jpg',  caption: 'Foto 6', label: 'foto (3).jpg',  accent: '#00ff88' },
  { id: 7, src: '/images/collage/foto (4).JPEG', caption: 'Foto 7', label: 'foto (4).JPEG', accent: '#00f5ff' },
  // Tambah lebih banyak foto di sini jika perlu:
  // { id: 7, src: "/images/collage/foto7.jpg", caption: 'Foto 7', label: 'foto7.jpg', accent: '#00f5ff' },
];

// Layout sizes — berulang bila foto lebih dari 6
const LAYOUTS = ['large', 'medium', 'medium', 'medium', 'large', 'medium'];

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function PhotoSlot({ photo, layoutSize, index, isShuffling }) {
  const [hov, setHov] = useState(false);
  const [imgError, setImgError] = useState(false);
  const isLarge = layoutSize === 'large';

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative',
        gridColumn: isLarge ? 'span 2' : 'span 1',
        height: isLarge ? 'clamp(220px, 28vw, 340px)' : 'clamp(160px, 20vw, 240px)',
        overflow: 'hidden',
        border: `1px solid ${hov ? photo.accent + '88' : C.border}`,
        transition: 'all .35s',
        boxShadow: hov ? `0 0 28px ${photo.accent}22` : 'none',
        opacity: isShuffling ? 0 : 1,
        transform: isShuffling ? 'scale(0.96)' : 'scale(1)',
      }}
    >
      {/* Corner accents */}
      {[[true,false,true,false],[true,false,false,true],[false,true,true,false],[false,true,false,true]].map((sides, ci) => (
        <div key={ci} style={{
          position: 'absolute', zIndex: 3,
          top: ci < 2 ? 8 : 'auto', bottom: ci >= 2 ? 8 : 'auto',
          left: (ci === 0 || ci === 2) ? 8 : 'auto',
          right: (ci === 1 || ci === 3) ? 8 : 'auto',
          width: 14, height: 14,
          borderTop:    sides[0] ? `2px solid ${photo.accent}66` : 'none',
          borderRight:  sides[1] ? `2px solid ${photo.accent}66` : 'none',
          borderBottom: sides[2] ? `2px solid ${photo.accent}66` : 'none',
          borderLeft:   sides[3] ? `2px solid ${photo.accent}66` : 'none',
          transition: 'border-color .3s',
          ...(hov ? { borderTopColor: photo.accent, borderRightColor: photo.accent, borderBottomColor: photo.accent, borderLeftColor: photo.accent } : {}),
        }} />
      ))}

      {photo.src && !imgError ? (
        <>
          <img
            src={photo.src}
            alt={photo.caption}
            onError={() => setImgError(true)}
            style={{
              width: '100%', height: '100%', objectFit: 'cover',
              transform: hov ? 'scale(1.06)' : 'scale(1)',
              transition: 'transform .45s ease',
              display: 'block',
            }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(to top, rgba(2,4,9,0.8) 0%, transparent 50%)`,
            opacity: hov ? 1 : 0.3, transition: 'opacity .3s',
          }} />
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '14px 16px',
            transform: hov ? 'translateY(0)' : 'translateY(60%)',
            transition: 'transform .3s ease',
          }}>
            <div style={{ fontFamily: "'Orbitron', monospace", fontSize: 11, fontWeight: 600, color: C.white }}>{photo.caption}</div>
          </div>
        </>
      ) : (
        // Placeholder
        <div style={{
          width: '100%', height: '100%',
          background: `linear-gradient(135deg, rgba(0,0,0,0.55), ${photo.accent}0a)`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <div style={{ fontSize: isLarge ? 40 : 30, opacity: 0.35 }}>📷</div>
          <div style={{ textAlign: 'center', padding: '0 12px' }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 3, color: photo.accent, textTransform: 'uppercase', marginBottom: 6, opacity: 0.85 }}>
              SLOT FOTO {index + 1}
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 2, color: C.gray, textTransform: 'uppercase', lineHeight: 1.9 }}>
              /public/images/collage/<br />
              <span style={{ color: C.white + 'bb' }}>{photo.label}</span>
            </div>
          </div>
          <div style={{ position: 'absolute', top: 10, right: 14, fontFamily: "'Orbitron', monospace", fontSize: isLarge ? 40 : 28, fontWeight: 900, color: photo.accent, opacity: 0.05, lineHeight: 1 }}>
            {String(index + 1).padStart(2, '0')}
          </div>
        </div>
      )}

      {/* Index badge */}
      <div style={{
        position: 'absolute', top: 10, left: 10, zIndex: 4,
        fontFamily: "'Space Mono', monospace", fontSize: 7,
        letterSpacing: 2, color: photo.accent,
        border: `1px solid ${photo.accent}44`,
        padding: '2px 7px', background: 'rgba(2,4,9,0.75)',
        textTransform: 'uppercase',
      }}>
        {String(index + 1).padStart(2, '0')}
      </div>
    </div>
  );
}

export default function PhotoCollage() {
  const [displayed, setDisplayed] = useState(ALL_PHOTOS.slice(0, 6));
  const [isShuffling, setIsShuffling] = useState(false);
  const [autoCount, setAutoCount] = useState(0);

  const doShuffle = useCallback(() => {
    setIsShuffling(true);
    setTimeout(() => {
      setDisplayed(shuffleArray(ALL_PHOTOS).slice(0, 6));
      setIsShuffling(false);
    }, 300);
  }, []);

  // Auto shuffle every 6 seconds if there are actual photos
  const hasRealPhotos = ALL_PHOTOS.some(p => p.src !== null);
  useEffect(() => {
    if (!hasRealPhotos || ALL_PHOTOS.length <= 6) return;
    const timer = setInterval(() => {
      setAutoCount(c => c + 1);
      doShuffle();
    }, 6000);
    return () => clearInterval(timer);
  }, [doShuffle, hasRealPhotos]);

  return (
    <section id="collage" style={{ background: 'rgba(4,8,16,0.9)' }}>
      {/* Header */}
      <div style={{ marginBottom: 36, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            fontFamily: "'Space Mono', monospace", fontSize: 10,
            letterSpacing: 4, textTransform: 'uppercase', color: C.cyan, marginBottom: 10,
          }}>
            <span style={{ width: 32, height: 1, background: C.cyan, display: 'inline-block' }} />
            Gallery
          </div>
          <h2 style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: 'clamp(22px, 3.5vw, 40px)',
            fontWeight: 700, color: C.white, letterSpacing: -1, marginBottom: 8,
          }}>
            Behind the <span style={{ color: C.cyan }}>Scenes</span>
          </h2>
          <p style={{ fontSize: 13, color: C.gray, fontFamily: "'Space Mono', monospace", letterSpacing: 1 }}>
            A glimpse into the journey — campus life to real-world projects.
          </p>
        </div>

        {/* Shuffle button */}
        <button
          onClick={doShuffle}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: "'Space Mono', monospace", fontSize: 9,
            letterSpacing: 2, textTransform: 'uppercase',
            color: C.cyan, background: 'rgba(0,245,255,0.04)',
            border: '1px solid rgba(0,245,255,0.25)',
            padding: '10px 20px', cursor: 'pointer',
            transition: 'all .25s', flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,245,255,0.08)'; e.currentTarget.style.borderColor = 'rgba(0,245,255,0.5)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(0,245,255,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,245,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(0,245,255,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <Shuffle size={12} style={{ animation: isShuffling ? 'rotate .4s linear' : 'none' }} />
          Shuffle Foto
        </button>
      </div>

      {/* Grid */}
      <div className="collage-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 10,
        transition: 'opacity .3s',
      }}>
        {displayed.map((photo, i) => (
          <PhotoSlot
            key={`${photo.id}-${autoCount}`}
            photo={photo}
            layoutSize={LAYOUTS[i % LAYOUTS.length]}
            index={i}
            isShuffling={isShuffling}
          />
        ))}
      </div>

      {/* Info footer */}
      <div style={{
        marginTop: 18, display: 'flex', alignItems: 'center', gap: 10,
        fontFamily: "'Space Mono', monospace", fontSize: 8,
        letterSpacing: 2, color: C.gray, textTransform: 'uppercase',
        borderTop: '1px solid rgba(0,245,255,0.06)', paddingTop: 14,
        flexWrap: 'wrap',
      }}>
        <RefreshCw size={10} />
        
      </div>

      <style>{`
        @media (max-width: 640px) {
          .collage-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 400px) {
          .collage-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
}
